/**
 * LabelPrinterSection — per-location label printer name (Phase 33-16 Wave D item Y)
 *
 * The v2 print-agent's StatusPanel says
 *   "Label printer: Not configured. Ask an admin to set it in IMS → Settings
 *    → Production → Label printer."
 * This section IS that admin surface. It writes
 * `locations.production_settings.label_printer_default_name` (string) for
 * each production-enabled location; the print-agent v2.x sidecar reads it
 * as `LABEL_PRINTER_NAME` env var on the next operator-config refresh.
 *
 * The value must match the OS-level printer name on the operator's PC
 * exactly — there is no normalization on the agent side. Helpful copy in
 * the input description points this out.
 *
 * UX notes (post 4-reviewer pass):
 *   * Per-row pending state via `updateMutation.variables?.locationId` so a
 *     save on row A does not lock row B.
 *   * Edit panel closes only on mutation success — if the save fails the
 *     operator sees the toast and the row stays in edit mode with their
 *     value still in the input.
 *   * Save button is disabled when the draft is empty or unchanged; never
 *     uses a toast as a validation surface.
 *   * `draft` resets from the prop whenever it changes externally (another
 *     tab, another admin) AND the row is not in active edit.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Loader2,
  AlertTriangle,
  RefreshCw,
  Save,
  X,
  RotateCcw,
  Printer,
  MapPin,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';

interface LocationRow {
  id: string;
  name: string;
  labelPrinterName: string | null;
}

interface LabelPrinterRowProps {
  location: LocationRow;
  /** Returns a promise so the row can keep the edit panel open until success. */
  onSave: (locationId: string, value: string) => Promise<void>;
  /** Returns a promise; same reason. */
  onClear: (locationId: string) => Promise<void>;
  /** True only when THIS row's mutation is in flight. */
  isSaving: boolean;
  /** False in Console single-location mode — the section header already names the location. */
  showName?: boolean;
}

function LabelPrinterRow({ location, onSave, onClear, isSaving, showName = true }: LabelPrinterRowProps) {
  const { t } = useTranslation('settings');
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(location.labelPrinterName ?? '');
  const hasOverride = location.labelPrinterName !== null;

  // Sync `draft` with the prop when the row is NOT in edit mode. Covers the
  // case where another admin (or another tab) updates the printer name and
  // the query invalidates → parent re-renders with a fresh prop. While
  // editing we deliberately preserve the operator's in-progress text.
  useEffect(() => {
    if (!isEditing) {
      setDraft(location.labelPrinterName ?? '');
    }
  }, [location.labelPrinterName, isEditing]);

  const beginEdit = () => {
    setDraft(location.labelPrinterName ?? '');
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setDraft(location.labelPrinterName ?? '');
    setIsEditing(false);
  };

  const trimmedDraft = draft.trim();
  const currentValue = location.labelPrinterName ?? '';
  const isEmpty = trimmedDraft.length === 0;
  const isUnchanged = trimmedDraft === currentValue;
  const canSave = !isEmpty && !isUnchanged && !isSaving;

  const handleSave = async () => {
    if (!canSave) return;
    try {
      await onSave(location.id, trimmedDraft);
      setIsEditing(false);
    } catch {
      // Mutation already surfaced an error toast in the parent's `onError`.
      // Keep `isEditing` true so the operator can fix and retry without
      // losing their input.
    }
  };

  const handleClear = async () => {
    try {
      await onClear(location.id);
    } catch {
      // Same as save — toast already fired; do nothing here.
    }
  };

  return (
    <div
      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 p-3 rounded-lg hover:bg-muted/30 focus-within:bg-muted/30 transition-colors"
      role="listitem"
    >
      <div className="flex items-center gap-2 min-w-0">
        {showName && <span className="font-medium text-sm truncate">{location.name}</span>}
        {hasOverride ? (
          <Badge variant="info" className="shrink-0">
            {t('production.labelPrinter.set')}
          </Badge>
        ) : (
          <Badge
            variant="outline"
            className="text-muted-foreground border-muted-foreground/30 font-normal shrink-0"
          >
            {t('production.labelPrinter.notConfigured')}
          </Badge>
        )}
      </div>

      <div className="flex items-center gap-2 sm:ml-auto">
        {isEditing ? (
          <div className="flex items-center gap-2">
            <Input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={t('production.labelPrinter.placeholder')}
              className="h-10 w-full sm:w-[260px]"
              aria-label={t('production.labelPrinter.inputLabel', { location: location.name })}
              autoFocus
              disabled={isSaving}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void handleSave();
                } else if (e.key === 'Escape') {
                  cancelEdit();
                }
              }}
            />
            <Button
              variant="default"
              size="sm"
              className="h-10 gap-1.5"
              onClick={() => void handleSave()}
              disabled={!canSave}
              aria-label={t('production.labelPrinter.saveAriaLabel', { location: location.name })}
            >
              {isSaving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Save className="h-3.5 w-3.5" />
              )}
              {t('production.labelPrinter.save')}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10 shrink-0"
              onClick={cancelEdit}
              disabled={isSaving}
              aria-label={t('production.labelPrinter.cancelAriaLabel', { location: location.name })}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <>
            <span
              className={cn(
                'text-sm font-mono',
                hasOverride ? 'text-foreground' : 'text-muted-foreground italic',
              )}
            >
              {hasOverride ? location.labelPrinterName : t('production.labelPrinter.noneItalic')}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-10 text-xs"
                onClick={beginEdit}
                disabled={isSaving}
                aria-label={t(
                  hasOverride
                    ? 'production.labelPrinter.changeAriaLabel'
                    : 'production.labelPrinter.setAriaLabel',
                  { location: location.name },
                )}
              >
                {hasOverride
                  ? t('production.labelPrinter.change')
                  : t('production.labelPrinter.set')}
              </Button>
              {hasOverride && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-10 w-10 text-muted-foreground hover:text-foreground"
                  onClick={() => void handleClear()}
                  disabled={isSaving}
                  aria-label={t('production.labelPrinter.clearAriaLabel', { location: location.name })}
                  title={t('production.labelPrinter.clearTooltip')}
                >
                  {isSaving ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <RotateCcw className="h-3.5 w-3.5" />
                  )}
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

interface LabelPrinterSectionProps {
  /**
   * Console single-location mode: when set, render only this location's row
   * and omit the per-row location name + the "Locations" summary header (the
   * Console section header already names the location). Absent = today's
   * full-list behavior.
   */
  locationId?: string;
}

export function LabelPrinterSection({ locationId }: LabelPrinterSectionProps = {}) {
  const { t } = useTranslation('settings');
  const locationsQuery = trpc.productionSettings.getLocationSettings.useQuery();
  const utils = trpc.useUtils();

  const updateMutation = trpc.productionSettings.updateLocationLabelPrinter.useMutation({
    onSuccess: (_data, variables) => {
      const cleared = variables.labelPrinterName === null;
      const loc = locationsQuery.data?.find((l) => l.id === variables.locationId);
      const locName = loc?.name ?? t('production.labelPrinter.locationFallback');
      toast.success(
        cleared
          ? t('production.labelPrinter.toastCleared', { location: locName })
          : t('production.labelPrinter.toastSaved', { location: locName }),
      );
      void utils.productionSettings.getLocationSettings.invalidate();
    },
    onError: (error) => {
      toast.error(t('production.labelPrinter.toastFailedDetail', { message: error.message }));
    },
  });

  // Per-row pending tracker — the mutation's `variables` are the args of the
  // in-flight call. When idle, `variables` is undefined; when pending, it
  // carries the `locationId` we're saving for. Pass `isSaving={pendingId === id}`
  // to each row so a save on row A doesn't lock row B's buttons.
  const pendingLocationId =
    updateMutation.isPending ? updateMutation.variables?.locationId ?? null : null;

  const productionLocations: LocationRow[] = (locationsQuery.data ?? [])
    .filter((l) => l.productionEnabled && (!locationId || l.id === locationId))
    .map((l) => ({
      id: l.id,
      name: l.name,
      labelPrinterName: l.labelPrinterName ?? null,
    }));

  const handleSave = (locationId: string, value: string) =>
    updateMutation.mutateAsync({ locationId, labelPrinterName: value }).then(() => undefined);

  const handleClear = (locationId: string) =>
    updateMutation.mutateAsync({ locationId, labelPrinterName: null }).then(() => undefined);

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2">
        <Printer className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" aria-hidden="true" />
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">
            {t('production.labelPrinter.description')}
          </p>
          <p className="text-xs text-muted-foreground">
            {t('production.labelPrinter.exactMatchWarning')}
          </p>
        </div>
      </div>

      {locationsQuery.isError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t('production.labelPrinter.loadFailedTitle')}</AlertTitle>
          <AlertDescription className="flex items-center gap-2">
            {locationsQuery.error?.message ?? t('production.labelPrinter.loadFailedBody')}
            <Button
              variant="link"
              className="p-0 h-auto"
              onClick={() => void locationsQuery.refetch()}
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              {t('production.labelPrinter.retry')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {locationsQuery.isLoading && (
        <div className="flex items-center justify-center py-6" role="status" aria-busy="true">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          <span className="ml-2 text-sm text-muted-foreground">
            {t('production.labelPrinter.loading')}
          </span>
        </div>
      )}

      {!locationsQuery.isLoading && !locationsQuery.isError && productionLocations.length === 0 && (
        <p className="text-sm text-muted-foreground py-4">
          {t('production.labelPrinter.noLocations')}
        </p>
      )}

      {!locationsQuery.isLoading && !locationsQuery.isError && productionLocations.length > 0 && (
        <div className="space-y-3">
          {!locationId && (
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
              <div>
                <h4 className="font-medium text-base">{t('production.labelPrinter.locationsTitle')}</h4>
                <p className="text-xs text-muted-foreground">
                  {t('production.labelPrinter.locationsHelp')}
                </p>
              </div>
            </div>
          )}
          <div className="space-y-1" role="list" aria-label={t('production.labelPrinter.listAria')}>
            {productionLocations.map((location) => (
              <LabelPrinterRow
                key={location.id}
                location={location}
                onSave={handleSave}
                onClear={handleClear}
                isSaving={pendingLocationId === location.id}
                showName={!locationId}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
